import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Settings,
  BookOpen,
  CalendarDays,
  ChevronRight,
  Loader2,
  LogOut,
  Play,
  Sparkles,
  Target,
  TrendingUp,
  Volume2,
} from "lucide-react";
import {
  StudentMobileShell,
  STUDENT_COLORS,
} from "../components/student/StudentMobileShell";
import { AuthService } from "../lib/auth";
import {
  getAllScores,
  type ScoresResponse,
} from "../lib/scores-api";
import {
  buildCourseProgress,
  getLectureNumber,
  getRecommendedScenario,
  getScenarioBestScores,
  type StudentScenarioResponse,
} from "../lib/student-course-progress";
import type { UserProfile } from "../types";
import { ProfileImage, ProfileImageEditor } from '../components/student/ProfileImage';

const cardStyle = {
  background: STUDENT_COLORS.surface,
  border: `1px solid ${STUDENT_COLORS.line}`,
  borderRadius: 12,
  padding: 16,
} as const;


function ProgressBar({ value }: { value: number }) {
  return (
    <div
      style={{
        height: 6,
        borderRadius: 999,
        background: STUDENT_COLORS.line,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${Math.max(0, Math.min(100, value))}%`,
          height: "100%",
          borderRadius: 999,
          background: STUDENT_COLORS.cyan,
        }}
      />
    </div>
  );
}


export function StudentHome() {
  const [, setLocation] = useLocation();
  const { data, isLoading } = useQuery<ScoresResponse>({
    queryKey: ["student-mobile", "scores", "all"],
    queryFn: getAllScores,
    staleTime: 60_000,
    refetchOnMount: "always",
  });
  const { data: scenarioData, isLoading: scenariosLoading } =
    useQuery<StudentScenarioResponse>({
      queryKey: ["/api/scenarios"],
      staleTime: 30_000,
    });
  const categoryProgress = buildCourseProgress(scenarioData, data?.scores);
  const scenarioBestScores = getScenarioBestScores(
    scenarioData,
    data?.scores,
  );
  const recommendedScenario = getRecommendedScenario(
    scenarioData,
    data?.scores,
  );
  const recommendedCourse = categoryProgress.find(
    (course) =>
      course.courseCategoryId === recommendedScenario?.courseCategoryId,
  );
  const recommendedLectureNumber = recommendedScenario
    ? getLectureNumber(scenarioData, recommendedScenario.id)
    : undefined;
  const recommendedDescription =
    recommendedScenario?.summary?.trim() ||
    recommendedScenario?.tasks[0]?.title ||
    "患者情報を確認して面接に取り組みます。";
  const completed = categoryProgress.reduce((sum, item) => sum + item.done, 0);
  const curriculumTotal = categoryProgress.reduce(
    (sum, item) => sum + item.total,
    0,
  );
  const progress =
    curriculumTotal > 0
      ? Math.round((completed / curriculumTotal) * 100)
      : 0;
  const progressLoading = isLoading || scenariosLoading;

  return (
    <StudentMobileShell active="home" eyebrow="HOME" title="ホーム">
      <section style={{ ...cardStyle, marginBottom: 14, padding: 18 }}>
        <h1
          style={{
            margin: 0,
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 22,
            fontWeight: 900,
          }}
        >
          <Target size={22} color={STUDENT_COLORS.cyan} />
          次のおすすめ面接
        </h1>
        {progressLoading ? (
          <div
            style={{
              marginTop: 14,
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: STUDENT_COLORS.muted,
              fontSize: 12.5,
            }}
          >
            <Loader2 className="animate-spin" size={17} />
            学習状況を確認しています
          </div>
        ) : recommendedScenario && recommendedCourse ? (
          <div style={{ marginTop: 14 }}>
            <div
              style={{
                color: STUDENT_COLORS.cyan,
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: "0.08em",
              }}
            >
              次に取り組む講座
            </div>
            <h2
              style={{
                margin: "7px 0 0",
                fontSize: 16,
                fontWeight: 900,
                lineHeight: 1.5,
              }}
            >
              {recommendedScenario.lectureTitle?.trim() ? (
                <>
                  <span style={{ color: STUDENT_COLORS.cyan }}>
                    {recommendedCourse.label}
                  </span>
                  <span style={{ color: STUDENT_COLORS.muted }}>：</span>
                  {recommendedScenario.lectureTitle.trim()}
                </>
              ) : (
                `${recommendedCourse.label}・講座${recommendedLectureNumber ?? "-"}`
              )}
            </h2>
            <p
              style={{
                margin: "8px 0 0",
                color: STUDENT_COLORS.muted,
                fontSize: 12.5,
                lineHeight: 1.7,
              }}
            >
              {recommendedDescription}
            </p>
            <div
              style={{
                marginTop: 8,
                color: STUDENT_COLORS.muted,
                fontSize: 11.5,
                fontWeight: 800,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {scenarioBestScores.has(recommendedScenario.id)
                ? `現在の最高得点 ${scenarioBestScores.get(recommendedScenario.id)}点`
                : "まだ受講していません"}
            </div>
            <button
              type="button"
              onClick={() => {
                window.sessionStorage.setItem(
                  "student-task-briefing",
                  JSON.stringify({
                    instructionId: recommendedScenario.id,
                    categoryName: recommendedCourse.label,
                    patientName: "患者",
                    taskTitle: recommendedDescription,
                    taskText: recommendedDescription,
                    questionnaireText: null,
                  }),
                );
                setLocation(
                  `/mypage/task-briefing?instructionId=${recommendedScenario.id}`,
                );
              }}
              style={{
                marginTop: 14,
                width: "100%",
                border: 0,
                borderRadius: 8,
                background: STUDENT_COLORS.ink,
                color: STUDENT_COLORS.surface,
                padding: "11px 18px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 900,
              }}
            >
              <Play size={16} fill="currentColor" />
              この面接に挑戦する
            </button>
          </div>
        ) : curriculumTotal > 0 ? (
          <div
            style={{
              marginTop: 14,
              padding: 14,
              borderRadius: 10,
              background: STUDENT_COLORS.greenSoft,
              color: STUDENT_COLORS.green,
              fontSize: 13,
              fontWeight: 900,
              lineHeight: 1.7,
            }}
          >
            利用可能な面接をすべてクリアしました。
          </div>
        ) : (
          <p
            style={{
              margin: "14px 0 0",
              color: STUDENT_COLORS.muted,
              fontSize: 12.5,
              lineHeight: 1.7,
            }}
          >
            管理画面で課題文が承認されると、おすすめを表示します。
          </p>
        )}
      </section>

      <section style={{ ...cardStyle, marginBottom: 14, padding: 18 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>学習進捗</h2>
        <div
          style={{
            marginTop: 18,
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 18,
            alignItems: "center",
          }}
        >
          <div
            style={{
              position: "relative",
              width: 170,
              height: 170,
              margin: "0 auto",
            }}
          >
            <svg
              viewBox="0 0 120 120"
              width="170"
              height="170"
              role="img"
              aria-label={`学習進捗${progress}パーセント`}
            >
              <circle
                cx="60"
                cy="60"
                r="49"
                fill="none"
                stroke={STUDENT_COLORS.line}
                strokeWidth="10"
              />
              <circle
                cx="60"
                cy="60"
                r="49"
                fill="none"
                stroke={STUDENT_COLORS.cyan}
                strokeWidth="10"
                strokeLinecap="round"
                pathLength="100"
                strokeDasharray={`${progress} 100`}
                transform="rotate(-90 60 60)"
              />
            </svg>
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "grid",
                placeItems: "center",
                textAlign: "center",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 38,
                    lineHeight: 1,
                    fontWeight: 900,
                    color: STUDENT_COLORS.cyan,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {progressLoading ? "-" : `${progress}%`}
                </div>
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 15,
                    fontWeight: 900,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {completed}/{curriculumTotal}
                </div>
                <div style={{ marginTop: 2, fontSize: 11.5, fontWeight: 700 }}>
                  講座
                </div>
              </div>
            </div>
          </div>
          <div>
            <div
              style={{ display: "grid", gap: 9, fontSize: 14, fontWeight: 800 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>利用可能講座</span>
                <span>{curriculumTotal}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>クリア済み講座</span>
                <span>{completed}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setLocation("/mypage/scores")}
              style={{
                marginTop: 18,
                width: "100%",
                border: 0,
                borderRadius: 8,
                padding: "11px 14px",
                background: STUDENT_COLORS.cyanSoft,
                color: STUDENT_COLORS.cyan,
                fontSize: 13,
                fontWeight: 900,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
              }}
            >
              <TrendingUp size={17} />
              学習推移
            </button>
          </div>
        </div>
      </section>

      <section style={{ ...cardStyle, padding: 18 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>
          カテゴリ別 学習状況
        </h2>
        <div style={{ marginTop: 12 }}>
          {categoryProgress.map((item, index) => {
            const percent = item.percent;
            return (
              <button
                key={item.courseCategoryId}
                type="button"
                onClick={() =>
                  setLocation(
                    `/mypage/learning?courseCategoryId=${item.courseCategoryId}`,
                  )
                }
                style={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns:
                    "42px minmax(84px, 1fr) minmax(104px, 1.2fr) 18px",
                  alignItems: "center",
                  gap: 10,
                  padding: "13px 0",
                  borderTop: 0,
                  borderLeft: 0,
                  borderRight: 0,
                  borderBottom:
                    index === categoryProgress.length - 1
                      ? "none"
                      : `1px solid ${STUDENT_COLORS.line}`,
                  background: "transparent",
                  color: STUDENT_COLORS.ink,
                  fontFamily: "inherit",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 8,
                    display: "grid",
                    placeItems: "center",
                    color: STUDENT_COLORS.cyan,
                    background: STUDENT_COLORS.cyanSoft,
                  }}
                >
                  {index % 2 === 0 ? (
                    <BookOpen size={19} />
                  ) : (
                    <Sparkles size={19} />
                  )}
                </span>
                <span
                  style={{ fontSize: 13.5, fontWeight: 900, lineHeight: 1.35 }}
                >
                  {item.label}
                </span>
                <div>
                  <div
                    style={{
                      marginBottom: 6,
                      textAlign: "right",
                      fontSize: 11.5,
                      fontWeight: 800,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {item.total === 0
                      ? "未準備（0/0）"
                      : `${percent}%（${item.done}/${item.total}）`}
                  </div>
                  <ProgressBar value={percent} />
                </div>
                <ChevronRight size={18} color={STUDENT_COLORS.muted} />
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => setLocation("/mypage/learning")}
          style={{
            marginTop: 14,
            width: "100%",
            minHeight: 48,
            border: 0,
            borderRadius: 999,
            background: `linear-gradient(135deg, ${STUDENT_COLORS.orangeFrom} 0%, ${STUDENT_COLORS.orangeTo} 100%)`,
            color: "#FFFFFF",
            fontSize: 14,
            fontWeight: 800,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <Play size={17} fill="currentColor" />
          学習を始める
        </button>
      </section>
    </StudentMobileShell>
  );
}

export { default as StudentScores } from './student-course-scores';

export function StudentSettings() {
  const [, setLocation] = useLocation();
  const [goalMinutes, setGoalMinutes] = useState(
    () => Number(localStorage.getItem("student-goal-minutes")) || 30,
  );
  const [sound, setSound] = useState(
    () => localStorage.getItem("student-sound") !== "off",
  );
  const [notifications, setNotifications] = useState(
    () => localStorage.getItem("student-notifications") !== "off",
  );
  useEffect(() => {
    localStorage.setItem("student-goal-minutes", String(goalMinutes));
  }, [goalMinutes]);
  useEffect(() => {
    localStorage.setItem("student-sound", sound ? "on" : "off");
  }, [sound]);
  useEffect(() => {
    localStorage.setItem("student-notifications", notifications ? "on" : "off");
  }, [notifications]);
  const row = (
    label: string,
    description: string,
    checked: boolean,
    onChange: (value: boolean) => void,
  ) => (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 0",
        borderBottom: `1px solid ${STUDENT_COLORS.line}`,
        cursor: "pointer",
      }}
    >
      <span style={{ flex: 1 }}>
        <span style={{ display: "block", fontSize: 13, fontWeight: 800 }}>
          {label}
        </span>
        <span
          style={{
            display: "block",
            marginTop: 4,
            color: STUDENT_COLORS.muted,
            fontSize: 11.5,
            lineHeight: 1.5,
          }}
        >
          {description}
        </span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        style={{ width: 22, height: 22, accentColor: STUDENT_COLORS.cyan }}
      />
    </label>
  );
  return (
    <StudentMobileShell
      active="settings"
      eyebrow="LEARNING SETTINGS"
      title="学習設定"
    >
      <button type="button" onClick={() => setLocation("/mypage/profile")} style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 16, padding: "8px 12px", background: STUDENT_COLORS.surface, color: STUDENT_COLORS.ink, border: `1px solid ${STUDENT_COLORS.line}`, borderRadius: 8, cursor: "pointer" }}><ArrowLeft size={16} />マイページへ戻る</button>
      <div style={{ marginBottom: 18 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.15em",
            color: STUDENT_COLORS.muted,
          }}
        >
          LEARNING SETTINGS
        </div>
        <h1 style={{ margin: "6px 0 0", fontSize: 26, fontWeight: 900 }}>
          学習設定
        </h1>
        <p
          style={{
            margin: "8px 0 0",
            color: STUDENT_COLORS.muted,
            fontSize: 13.5,
            lineHeight: 1.75,
          }}
        >
          無理なく続けられる目標を設定します。
        </p>
      </div>
      <section style={{ ...cardStyle, marginBottom: 14, padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 42,
              height: 42,
              borderRadius: 10,
              display: "grid",
              placeItems: "center",
              background: STUDENT_COLORS.cyanSoft,
              color: STUDENT_COLORS.cyan,
            }}
          >
            <Target size={20} />
          </span>
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.15em",
                color: STUDENT_COLORS.muted,
              }}
            >
              DAILY GOAL
            </div>
            <h2 style={{ margin: "5px 0 0", fontSize: 16, fontWeight: 900 }}>
              1日の目標時間
            </h2>
          </div>
        </div>
        <div
          style={{ marginTop: 20, display: "flex", gap: 8, flexWrap: "wrap" }}
        >
          {[10, 20, 30, 45, 60].map((minutes) => (
            <button
              key={minutes}
              type="button"
              onClick={() => setGoalMinutes(minutes)}
              style={{
                minWidth: 68,
                border: `1px solid ${goalMinutes === minutes ? STUDENT_COLORS.ink : STUDENT_COLORS.line}`,
                borderRadius: 8,
                padding: "10px 12px",
                background:
                  goalMinutes === minutes
                    ? STUDENT_COLORS.ink
                    : STUDENT_COLORS.surface,
                color:
                  goalMinutes === minutes
                    ? STUDENT_COLORS.surface
                    : STUDENT_COLORS.ink,
                fontSize: 13,
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              {minutes}分
            </button>
          ))}
        </div>
        <div
          style={{
            marginTop: 18,
            padding: 14,
            borderRadius: 10,
            background: STUDENT_COLORS.greenSoft,
            color: STUDENT_COLORS.green,
            fontSize: 12.5,
            fontWeight: 700,
            lineHeight: 1.7,
          }}
        >
          毎日{goalMinutes}分を続けると、週に約
          {Math.round(((goalMinutes * 7) / 60) * 10) / 10}時間の練習になります。
        </div>
      </section>
      <section style={{ ...cardStyle, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <CalendarDays size={20} color={STUDENT_COLORS.purple} />
          <div>
            <strong style={{ display: "block", fontSize: 14 }}>
              学習リマインダー
            </strong>
            <span
              style={{
                display: "block",
                marginTop: 4,
                color: STUDENT_COLORS.muted,
                fontSize: 12,
              }}
            >
              平日 18:00 にお知らせ
            </span>
          </div>
        </div>
      </section>
      <section style={cardStyle}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 14,
            fontWeight: 800,
          }}
        >
          <Volume2 size={18} color={STUDENT_COLORS.cyan} />
          端末設定
        </div>
        <div style={{ marginTop: 8 }}>
          {row(
            "音声を再生する",
            "患者アバターの返答を音声で再生します。",
            sound,
            setSound,
          )}
          {row(
            "お知らせを受け取る",
            "メンテナンスなどの案内を表示します。",
            notifications,
            setNotifications,
          )}
        </div>
      </section>
    </StudentMobileShell>
  );
}

export function StudentProfile() {
  const [, setLocation] = useLocation();
  const auth = AuthService.getInstance();
  const { data: user } = useQuery<UserProfile | null>({
    queryKey: ["currentUser"],
    queryFn: () => auth.getCurrentUser(),
    staleTime: 300_000,
  });
  const logout = async () => {
    await auth.logout();
    setLocation("/login");
  };
  return (
    <StudentMobileShell active="profile" eyebrow="MY PAGE" title="マイページ">
      <button type="button" onClick={() => setLocation("/mypage/settings")} style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 16, padding: "8px 12px", background: STUDENT_COLORS.surface, color: STUDENT_COLORS.ink, border: `1px solid ${STUDENT_COLORS.line}`, borderRadius: 8, cursor: "pointer" }}><Settings size={16} />学習設定<ChevronRight size={16} /></button>
      <div style={{ marginBottom: 18 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.15em",
            color: STUDENT_COLORS.muted,
          }}
        >
          MY PAGE
        </div>
        <h1 style={{ margin: "6px 0 0", fontSize: 26, fontWeight: 900 }}>
          マイページ
        </h1>
        <p
          style={{
            margin: "8px 0 0",
            color: STUDENT_COLORS.muted,
            fontSize: 13.5,
            lineHeight: 1.75,
          }}
        >
          登録情報と最近の学習状況を確認できます。
        </p>
      </div>
      <section style={{ ...cardStyle, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span
            style={{
              width: 54,
              height: 54,
              borderRadius: 999,
              display: "grid",
              placeItems: "center",
              background: STUDENT_COLORS.cyanSoft,
              color: STUDENT_COLORS.cyan,
            }}
          >
            <ProfileImage size={54} />
          </span>
          <div>
            <strong style={{ display: "block", fontSize: 17 }}>
              {user?.name || "学生"}
            </strong>
            <span
              style={{
                display: "block",
                marginTop: 4,
                color: STUDENT_COLORS.muted,
                fontSize: 12,
              }}
            >
              {user?.participantCode
                ? `学籍番号 ${user.participantCode}`
                : user?.email || ""}
            </span>
          </div>
        </div>
        <ProfileImageEditor />
      </section>
      <section style={{ ...cardStyle, marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 900 }}>最近の学習</h2>
        <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              fontSize: 13,
            }}
          >
            <span>修了カリキュラム</span>
            <strong style={{ fontVariantNumeric: "tabular-nums" }}>
              0 / 26
            </strong>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              fontSize: 13,
            }}
          >
            <span>メールアドレス</span>
            <strong
              style={{
                maxWidth: "60%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user?.email || "-"}
            </strong>
          </div>
        </div>
      </section>
      <button
        type="button"
        onClick={logout}
        style={{
          width: "100%",
          minHeight: 46,
          borderRadius: 8,
          border: `1px solid ${STUDENT_COLORS.line}`,
          background: STUDENT_COLORS.surface,
          color: STUDENT_COLORS.ink,
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        <LogOut size={17} />
        ログアウト
      </button>
    </StudentMobileShell>
  );
}
